// REAL. The A3 toolkit panel — the kit view + browsable library.
//
// This screen replaces the old /confirm "commit beat" with the actual
// panel the actor manages their kit in. Copy locked per Cael's
// register pass (cael-A3-toolkit-panel-register-pass-2026-06-05.md);
// data shape is the post-A1 aggregator (signal_case + tools[] + browsable_library[]
// + room/kind/answered_for/lineage_chains/citation_state).
//
// Spine (per Cael):
//   - Recognition line "A mirror, not a verdict." (4-word panel header).
//   - Spine line "Add what's yours, set aside what isn't — whenever."
//   - "set aside" is the mute-not-delete discipline — verb stays.
//   - "You reached for this." tags only on answered_for tools; bundled
//     tools have NO tag (silence is correct — don't label them "suggested").
//   - Group labels are actor-facing: Margins · Journal > Breakdown / Character
//     work; Drills > Method drills / Classics. Engineering names (drill_method,
//     drill_classic, "method-tagged", "always-on") never appear.
//   - Citation chains (lineage_chains) live behind the optional "?" — never in
//     the front reveal line (Tate's surface gating; we render the bare hint).
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToolWithMeta } from "../api/client";
import { useArc } from "../state/arc";

interface Group {
  label: string;
  sublabel?: string;
  filter: (t: ToolWithMeta) => boolean;
}

// Routing per Cael's group labels — order matters (top-down render).
const GROUPS: Array<{ section: string; sub: Group[] }> = [
  {
    section: "Margins · Journal",
    sub: [
      {
        label: "Breakdown",
        filter: (t) => t.room === "journal" && t.kind === "breakdown",
      },
      {
        label: "Character work",
        filter: (t) => t.room === "journal" && t.kind === "character",
      },
    ],
  },
  {
    section: "Drills",
    sub: [
      {
        label: "Method drills",
        filter: (t) => t.room === "drills" && t.kind === "drill_method",
      },
      {
        label: "Classics",
        sublabel: "always in the kit",
        filter: (t) => t.room === "drills" && t.kind === "drill_classic",
      },
    ],
  },
];

function ToolRow({
  tool,
  inKit,
  onToggle,
  showAnsweredFor,
}: {
  tool: ToolWithMeta;
  inKit: boolean; // true = in actor's kit (set-aside button); false = in library (add button)
  onToggle: () => void;
  showAnsweredFor: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="card" style={{ marginBottom: "0.5rem" }}>
      <p style={{ marginBottom: "0.25rem" }}>
        <strong>{tool.name}.</strong> {tool.gist}
      </p>
      {showAnsweredFor && tool.answered_for && (
        // Past-tense, plain. NOT "Recommended"/"Top pick" — actor reached for
        // it; we only reflect that back.
        <p
          className="muted"
          style={{ fontSize: "0.85em", margin: "0.25rem 0" }}
        >
          You reached for this.
        </p>
      )}
      <div
        style={{
          display: "flex",
          gap: "0.5rem",
          alignItems: "center",
          marginTop: "0.4rem",
        }}
      >
        <button
          onClick={onToggle}
          style={{
            background: inKit ? "transparent" : undefined,
            border: inKit ? "1px solid #999" : undefined,
            fontSize: "0.85em",
            padding: "0.25rem 0.6rem",
          }}
        >
          {inKit ? "Set aside" : "Add to kit"}
        </button>
        {tool.lineage_chains.length > 0 && (
          <button
            onClick={() => setExpanded((x) => !x)}
            style={{
              background: "transparent",
              border: "none",
              color: "#666",
              fontSize: "0.85em",
              padding: "0.25rem 0.4rem",
              cursor: "pointer",
            }}
            aria-label="Show lineage"
          >
            {expanded ? "× close" : "?"}
          </button>
        )}
      </div>
      {expanded && tool.lineage_chains.length > 0 && (
        // Citations behind the "?" — never in the front reveal line. Yellow
        // chains carry a quiet flag so the actor knows the wording is method-
        // faithful pending primary citation.
        <div
          className="muted"
          style={{ fontSize: "0.85em", marginTop: "0.5rem" }}
        >
          {tool.lineage_chains.map((c, i) => (
            <p key={i} style={{ margin: "0.25rem 0" }}>
              {c.chain_string}
              {c.citation_state === "yellow" && (
                <span style={{ marginLeft: "0.4rem", opacity: 0.7 }}>
                  · method-faithful
                </span>
              )}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ToolkitPanel() {
  const navigate = useNavigate();
  const { state, update, reset } = useArc();
  const result = state.quizResult;
  const [setAside, setSetAside] = useState<Set<string>>(new Set());
  const [added, setAdded] = useState<Set<string>>(new Set());

  if (!result || !state.methodologyId) {
    return (
      <div className="v2"><section>
        <h2>Let's set this up first.</h2>
        <p className="muted">
          We need to know how you work before we get into a scene.
        </p>
        <button
          onClick={() => {
            reset();
            navigate("/welcome");
          }}
        >
          ← Back to the start
        </button>
      </section></div>
    );
  }

  // Kit = aggregator tools[] (the actor's voted-for union) ∪ added — minus set-aside.
  // Library = browsable_library[] minus everything currently in kit.
  const aggregatorTools = result.tools ?? [];
  const fullLibrary = result.browsable_library ?? [];
  const aggregatorByName = useMemo(() => {
    const m = new Map<string, ToolWithMeta>();
    for (const t of aggregatorTools) m.set(t.name, t);
    return m;
  }, [aggregatorTools]);
  const libraryByName = useMemo(() => {
    const m = new Map<string, ToolWithMeta>();
    for (const t of fullLibrary) m.set(t.name, t);
    return m;
  }, [fullLibrary]);

  const kit: ToolWithMeta[] = useMemo(() => {
    const out: ToolWithMeta[] = [];
    for (const t of aggregatorTools) {
      if (!setAside.has(t.name)) out.push(t);
    }
    for (const name of added) {
      if (setAside.has(name)) continue;
      if (aggregatorByName.has(name)) continue; // already in
      const t = libraryByName.get(name);
      if (t) out.push(t);
    }
    return out;
  }, [aggregatorTools, added, setAside, aggregatorByName, libraryByName]);

  const kitNames = useMemo(() => new Set(kit.map((t) => t.name)), [kit]);
  const libraryRemaining: ToolWithMeta[] = useMemo(
    () => fullLibrary.filter((t) => !kitNames.has(t.name)),
    [fullLibrary, kitNames],
  );

  const setAsideTool = (name: string) => {
    setSetAside((s) => {
      const next = new Set(s);
      next.add(name);
      return next;
    });
    setAdded((s) => {
      // If we added it via library, also drop the add (so set-aside undoes
      // an add cleanly).
      if (s.has(name)) {
        const next = new Set(s);
        next.delete(name);
        return next;
      }
      return s;
    });
  };
  const addTool = (name: string) => {
    setSetAside((s) => {
      if (s.has(name)) {
        const next = new Set(s);
        next.delete(name);
        return next;
      }
      return s;
    });
    setAdded((s) => {
      const next = new Set(s);
      next.add(name);
      return next;
    });
  };

  // Fast lookup for whether to render the answered_for tag — only on tools
  // that are originally in the aggregator's tools[] (the actor's voted union).
  // Library-added tools never carry "You reached for this." (they weren't
  // reached for; the actor browsed and added).
  const isFromAggregator = (name: string) => aggregatorByName.has(name);

  const lin = result.lineage;

  return (
    <section>
      {/* Cael's locked recognition line — the spine of the reveal in 4 words. */}
      <h2 style={{ marginBottom: "0.25rem" }}>A mirror, not a verdict.</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        The tools you already reach for — and the rest of the room, whenever
        you want it.
      </p>

      {/* Tradition micro-pointer (for context, not for verdict). Convergent
          case names the one tradition; mixed names both with the short names
          per A2 v2-LIVE. */}
      {result.signal_case !== "unsure" && result.lineages.length > 0 && (
        <p style={{ marginTop: "1rem" }}>
          {result.signal_case === "convergent" ? (
            <>
              Your work runs through{" "}
              <strong>{result.lineages[0].tradition_short_name}</strong>.
            </>
          ) : (
            <>
              Your work draws on{" "}
              {result.lineages.map((l, i) => (
                <span key={l.id}>
                  {i > 0 && (i === result.lineages.length - 1 ? " and " : ", ")}
                  <strong>{l.tradition_short_name}</strong>
                </span>
              ))}
              .
            </>
          )}
        </p>
      )}
      {result.signal_case === "unsure" && (
        <p style={{ marginTop: "1rem" }}>
          You've got the core every approach shares — the foundation, not a
          narrowing. Add tools as you reach for them.
        </p>
      )}

      {/* The spine line — standing freedom, "set aside" verb is load-bearing. */}
      <p className="muted" style={{ marginTop: "1rem", fontStyle: "italic" }}>
        Add what's yours, set aside what isn't — whenever.
      </p>

      {/* The kit, grouped per Cael's actor-facing labels. */}
      <div style={{ marginTop: "1.5rem" }}>
        <h3>Your kit</h3>
        {GROUPS.map((section) => {
          // Only render the section if any tool in any of its sub-groups is in the kit.
          const sectionTools = section.sub.flatMap((sub) =>
            kit.filter(sub.filter),
          );
          if (sectionTools.length === 0) return null;
          return (
            <div key={section.section} style={{ marginBottom: "1.5rem" }}>
              <h4 style={{ marginBottom: "0.5rem" }}>{section.section}</h4>
              {section.sub.map((sub) => {
                const subTools = kit.filter(sub.filter);
                if (subTools.length === 0) return null;
                return (
                  <div key={sub.label} style={{ marginBottom: "1rem" }}>
                    <p
                      className="muted"
                      style={{
                        fontSize: "0.9em",
                        margin: "0.5rem 0 0.25rem 0",
                      }}
                    >
                      {sub.label}
                      {sub.sublabel && (
                        <span style={{ opacity: 0.7 }}>
                          {" "}
                          · {sub.sublabel}
                        </span>
                      )}
                    </p>
                    {subTools.map((t) => (
                      <ToolRow
                        key={t.name}
                        tool={t}
                        inKit={true}
                        onToggle={() => setAsideTool(t.name)}
                        showAnsweredFor={isFromAggregator(t.name)}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* The rest of the room — browsable_library minus what's in the kit. */}
      {libraryRemaining.length > 0 && (
        <div style={{ marginTop: "1.5rem" }}>
          <h3>The rest of the room</h3>
          <p className="muted">
            Any tool, any tradition — yours when you reach for it.
          </p>
          {GROUPS.map((section) => {
            const sectionTools = section.sub.flatMap((sub) =>
              libraryRemaining.filter(sub.filter),
            );
            if (sectionTools.length === 0) return null;
            return (
              <div key={section.section} style={{ marginBottom: "1.5rem" }}>
                <h4 style={{ marginBottom: "0.5rem" }}>{section.section}</h4>
                {section.sub.map((sub) => {
                  const subTools = libraryRemaining.filter(sub.filter);
                  if (subTools.length === 0) return null;
                  return (
                    <div key={sub.label} style={{ marginBottom: "1rem" }}>
                      <p
                        className="muted"
                        style={{
                          fontSize: "0.9em",
                          margin: "0.5rem 0 0.25rem 0",
                        }}
                      >
                        {sub.label}
                        {sub.sublabel && (
                          <span style={{ opacity: 0.7 }}>
                            {" "}
                            · {sub.sublabel}
                          </span>
                        )}
                      </p>
                      {subTools.map((t) => (
                        <ToolRow
                          key={t.name}
                          tool={t}
                          inKit={false}
                          onToggle={() => addTool(t.name)}
                          showAnsweredFor={false}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {lin.analyzer_gap && (
        <p className="muted" style={{ marginTop: "1.5rem" }}>
          A note: your tradition's full set of tools is still being built into
          the deeper analysis. For now we'll work from the closest frame we
          have wired in, and add the rest as we build.
        </p>
      )}

      <div style={{ display: "flex", gap: "0.5rem", marginTop: "1.5rem" }}>
        <button
          onClick={() => {
            // Persist kit overrides to ArcState so downstream screens have it
            // (later — for now we just navigate; the panel reads result.tools
            // on revisit).
            update({});
            navigate("/upload");
          }}
        >
          Let me show you how this works →
        </button>
        <button
          onClick={() => {
            reset();
            navigate("/welcome");
          }}
          style={{ background: "transparent" }}
        >
          Let me try again
        </button>
      </div>
    </section>
  );
}
